/**
 * Hooks para CRUD de plantillas PDF.
 *
 * - usePdfTemplates(clientId) — lista los 3 templates del cliente.
 * - usePdfTemplate(clientId, docType) — get individual con fallback
 *   a default (el backend lo resuelve, retorna source: "db" | "default").
 * - useUpsertPdfTemplate() — mutation para PUT.
 * - useDeletePdfTemplate() — mutation para DELETE (vuelve a default).
 * - useDefaultPdfTemplate(docType) — read-only del default Taxor.
 *
 * Toasts (sonner) en mutations: success y error.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  pdfTemplatesApi,
  type DocType,
  type ListResponse,
  type PdfTemplate,
} from '@/lib/api-pdf-templates'

const queryKeys = {
  list: (clientId: string) => ['admin', 'pdf-templates', clientId] as const,
  get: (clientId: string, docType: DocType) =>
    ['admin', 'pdf-templates', clientId, docType] as const,
  default: (docType: DocType) =>
    ['admin', 'pdf-templates', 'default', docType] as const,
}

export function usePdfTemplates(clientId: string | undefined) {
  return useQuery<ListResponse<PdfTemplate>>({
    queryKey: queryKeys.list(clientId ?? ''),
    queryFn: () => pdfTemplatesApi.list(clientId!),
    enabled: !!clientId,
    staleTime: 60 * 1000,
  })
}

export function usePdfTemplate(clientId: string | undefined, docType: DocType) {
  return useQuery<PdfTemplate>({
    queryKey: queryKeys.get(clientId ?? '', docType),
    queryFn: () => pdfTemplatesApi.get(clientId!, docType),
    enabled: !!clientId,
    staleTime: 60 * 1000,
  })
}

export function useDefaultPdfTemplate(docType: DocType) {
  return useQuery<PdfTemplate>({
    queryKey: queryKeys.default(docType),
    queryFn: () => pdfTemplatesApi.getDefault(docType),
    staleTime: Infinity, // los defaults vienen del binario backend, no cambian sin redeploy
  })
}

interface UpsertVars {
  clientId: string
  docType: DocType
  html: string
}

export function useUpsertPdfTemplate() {
  const qc = useQueryClient()
  return useMutation<PdfTemplate, Error, UpsertVars>({
    mutationFn: ({ clientId, docType, html }) =>
      pdfTemplatesApi.upsert(clientId, docType, html),
    onSuccess: (_data, { clientId, docType }) => {
      qc.invalidateQueries({ queryKey: queryKeys.list(clientId) })
      qc.invalidateQueries({ queryKey: queryKeys.get(clientId, docType) })
      toast.success('Plantilla guardada')
    },
    onError: (err) => {
      const msg = extractMessage(err)
      toast.error(`No se pudo guardar: ${msg}`)
    },
  })
}

interface DeleteVars {
  clientId: string
  docType: DocType
}

export function useDeletePdfTemplate() {
  const qc = useQueryClient()
  return useMutation<void, Error, DeleteVars>({
    mutationFn: ({ clientId, docType }) =>
      pdfTemplatesApi.delete(clientId, docType),
    onSuccess: (_data, { clientId, docType }) => {
      qc.invalidateQueries({ queryKey: queryKeys.list(clientId) })
      qc.invalidateQueries({ queryKey: queryKeys.get(clientId, docType) })
      toast.success('Plantilla eliminada — vuelve al default Taxor')
    },
    onError: (err) => {
      const msg = extractMessage(err)
      toast.error(`No se pudo eliminar: ${msg}`)
    },
  })
}

function extractMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message || e?.message || 'error desconocido'
}
